const post = {
  id: "farms-to-code",
  title: "From Farms to Code: My Unconventional Path into Tech",
  date: "2026-04-22",
  excerpt: "My story doesn't start with code. It starts on a farm. Growing up there shaped a lot more than just my childhood.",
  content: `
My story doesn't start with code.

It starts on a farm.

Growing up there shaped a lot more than my childhood. It was where I learned what responsibility actually means—before I even had the words for it. Things didn't wait for you. Animals, land, work… they all needed attention every day. You show up, or things fall apart. Simple as that.

That environment became my foundation.

## Where Curiosity Started

Even though I grew up surrounded by physical work and nature, I was always curious about technology.

I built my first computer when I was around 12. I didn't really know what I was doing at the time, but I remember the feeling of wanting to understand what was happening inside the machine. That curiosity never really left.

During high school and university, I studied biology and environmental sciences. I was interested in understanding the natural world, but at the same time, I kept returning to technology in the background.

And while I studied, I kept working on the farm during summers and breaks. It wasn't separate from my life—it was just part of it. Early mornings, physical work, problem-solving in real time. That rhythm stays with you.

## Learning Systems in a Different Way

After university, I started working at a car parts company in supply management.

It was a very different world from the farm. Instead of physical work, I was dealing with processes, logistics, and systems that had to function smoothly together.

I started to see patterns—how one delay can affect everything, how structure matters, how efficiency is often invisible when it works but obvious when it doesn't.

Without realizing it, I was learning how systems behave. Something that later made programming feel strangely familiar.

## Coming Back to Tech

While working there, I started studying computer engineering on weekends in another city.

That was my first real step back into tech. It wasn't easy—working full-time during the week and studying on weekends—but I didn't want to let that curiosity fade again.

After that, I completed a Java developer bootcamp, where I finally started building things properly and learning how software development works in practice.

For the first time, I wasn't just curious about computers—I was actually creating with them.

## China: A Different Kind of Growth

After the bootcamp, life took a turn and I moved to China to teach English.

That experience changed me in ways I didn't expect.

Everything was different—language, culture, daily life. Even simple things required effort and patience. You learn quickly that you can't control everything, so you adapt instead.

During this time, I started learning Mandarin and made it part of my daily life. Slowly, it became less about "studying a language" and more about understanding a different way of thinking.

While teaching, I also did translation and proofreading work, which sharpened my attention to detail and language sensitivity even further.

## Back to Tech Again — But More Serious This Time

Later, while still in Shanghai, I joined a web development bootcamp.

This time it felt different. It wasn't curiosity anymore—it was direction.

I started building real projects, understanding structure, and seeing how everything I had done before connected in unexpected ways.

At some point, I realized I wasn't "switching into tech." I was returning to it—just through a longer route.

## What It All Connects To

Looking back, my path isn't linear, but it's connected.

- The farm taught me discipline and responsibility
- Biology taught me observation and thinking in systems
- Supply management taught me structure and processes
- Living abroad taught me adaptability
- Languages taught me patience and consistency
- Early curiosity kept pulling me back to technology

All of it feeds into how I approach development now.

Because building software isn't just technical—it's about understanding systems, solving problems, and creating something that works in the real world.

## Where I Am Now

Right now, I'm building.

Learning, creating projects, improving my skills, and slowly shaping a direction that feels like my own.

I'm not trying to force a path that looks perfect on paper. I'm building one that makes sense based on everything that came before.

And if there's one thing I've learned from where I come from, it's this:

You don't need a straight road to get somewhere meaningful.

You just need to keep going.
  `,
  tags: ["career", "journey", "self-improvement"],
}

export default post